import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Spinner, Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { python } from "@codemirror/lang-python";
import { markdown } from "@codemirror/lang-markdown";
import { sql } from "@codemirror/lang-sql";
import { fmRead, fmWrite } from "../../redux/actionCreator";

function langFor(name) {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (["js", "jsx", "ts", "tsx", "mjs", "cjs"].includes(ext)) return [javascript({ jsx: true, typescript: ext.includes("ts") })];
  if (ext === "json") return [json()];
  if (["html", "htm", "xml", "vue"].includes(ext)) return [html()];
  if (["css", "scss", "less"].includes(ext)) return [css()];
  if (ext === "py") return [python()];
  if (["md", "markdown"].includes(ext)) return [markdown()];
  if (ext === "sql") return [sql()];
  return [];
}

/**
 * Modal-based file editor.
 *
 * Props:
 *   projectId, serverId         — context
 *   filePath  string            — full path from base (e.g. "/etc/nginx.conf")
 *   readOnly  bool              — view mode (default false)
 *   tail      bool              — polls every 2s and shows latest content
 *   onClose   () => void
 *   onSaved   () => void        — fired after a successful save
 */
export default function FileEditor({
  projectId,
  serverId,
  filePath,
  readOnly = false,
  tail = false,
  onClose,
  onSaved,
}) {
  const dispatch = useDispatch();
  const [content, setContent] = useState("");
  const [original, setOriginal] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null); // { size, mtime, mode }
  const [livePoll, setLivePoll] = useState(tail);
  const intervalRef = useRef(null);

  const fileName = useMemo(() => (filePath ? filePath.split("/").pop() : ""), [filePath]);
  const extensions = useMemo(() => langFor(fileName), [fileName]);
  const dirty = !readOnly && content !== original;

  const load = useCallback(
    async (silent = false) => {
      if (!filePath) return;
      if (!silent) setLoading(true);
      const res = await dispatch(fmRead(projectId, serverId, filePath));
      if (!silent) setLoading(false);
      if (!res?.status) {
        setError(res?.message || "Failed to read");
        return;
      }
      setError(null);
      setMeta({ size: res.data.size, mtime: res.data.mtime, mode: res.data.mode });
      // Don't clobber unsaved edits during silent poll.
      if (silent && dirty) return;
      setContent(res.data.content);
      setOriginal(res.data.content);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, serverId, filePath, dispatch]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Live tail — poll every 2s while modal open and toggle is on.
  useEffect(() => {
    if (!livePoll) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      return;
    }
    intervalRef.current = setInterval(() => load(true), 2000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePoll, load]);

  const save = async () => {
    setSaving(true);
    const res = await dispatch(fmWrite(projectId, serverId, filePath, content));
    setSaving(false);
    if (res?.status) {
      setOriginal(content);
      onSaved && onSaved();
    } else {
      alert(res?.message || "Save failed");
    }
  };

  const tryClose = () => {
    if (dirty && !window.confirm("Discard unsaved changes?")) return;
    onClose && onClose();
  };

  return (
    <Modal
      show
      onHide={tryClose}
      size="xl"
      centered
      fullscreen="lg-down"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <strong>{readOnly ? "View" : "Edit"}</strong>
          <code className="small">{filePath}</code>
          {dirty && <span className="badge bg-warning text-dark">unsaved</span>}
          {meta && (
            <small className="text-muted">
              {meta.size} bytes · {new Date(meta.mtime).toLocaleString()}
            </small>
          )}
        </div>
      </Modal.Header>
      <Modal.Body style={{ padding: 0 }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : error ? (
          <div className="alert alert-danger m-3">{error}</div>
        ) : (
          <CodeMirror
            value={content}
            height="60vh"
            extensions={extensions}
            editable={!readOnly}
            onChange={(v) => setContent(v)}
            theme="dark"
            basicSetup={{
              lineNumbers: true,
              highlightActiveLine: true,
              foldGutter: true,
              autocompletion: !readOnly,
            }}
          />
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Form.Check
          type="switch"
          id="tail-toggle"
          label="Live (poll every 2s)"
          checked={livePoll}
          onChange={(e) => setLivePoll(e.target.checked)}
        />
        <div className="d-flex gap-2">
          <Button variant="light" onClick={tryClose}>
            Close
          </Button>
          {!readOnly && (
            <Button variant="primary" disabled={!dirty || saving} onClick={save}>
              {saving ? <Spinner size="sm" animation="border" /> : "Save"}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}
